import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";

type DisclosureChoice = "yes" | "no" | "declineToFurnish" | "";

export interface SpecialNeedsForm {
  disclosureChoice: DisclosureChoice;
  // Veteran status of the head of household (TAA Supplemental §3 "Are you a
  // veteran?"). Captured here rather than inferred from the aggregate
  // disclosure, which can't distinguish veterans from the other categories.
  isVeteran?: boolean | null;
  _acknowledgedAt?: number;
}

interface SpecialNeedsProps {
  defaultValues?: SpecialNeedsForm;
  onSubmit: (data: SpecialNeedsForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  // Present only in the co-applicant flow — the primary's already-submitted answer.
  primarySpecialNeeds?: SpecialNeedsForm;
}

const CATEGORY_KEYS = [
  "alcoholDrugAddiction",
  "coloniaResidents",
  "personsWithDisabilities",
  "vawa",
  "hivAids",
  "homeless",
  "veterans",
  "woundedWarriors",
  "farmworkers",
] as const;

const OPTIONS: { value: Exclude<DisclosureChoice, "">; labelKey: string }[] = [
  { value: "yes", labelKey: "optionYes" },
  { value: "no", labelKey: "optionNo" },
  { value: "declineToFurnish", labelKey: "optionDecline" },
];

export const SpecialNeeds: React.FC<SpecialNeedsProps> = ({
  defaultValues,
  onSubmit,
  onBack,
  isSaving = false,
  primarySpecialNeeds,
}) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(
    !!defaultValues?._acknowledgedAt
  );

  const schema = yup.object({
    disclosureChoice: yup
      .string()
      .oneOf(
        ["yes", "no", "declineToFurnish"],
        t("affordable.application.specialNeeds.answerRequired") as string
      )
      .required(
        t("affordable.application.specialNeeds.answerRequired") as string
      ),
    isVeteran: yup
      .boolean()
      .typeError(
        t("affordable.application.specialNeeds.veteranRequired") as string
      )
      .required(
        t("affordable.application.specialNeeds.veteranRequired") as string
      ),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SpecialNeedsForm>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: defaultValues || { disclosureChoice: "", isVeteran: null },
  });

  // ── Co-applicant read-only + acknowledgment view ──────────────────────────
  if (primarySpecialNeeds) {
    const primary = primarySpecialNeeds;
    const selectedLabelKey =
      OPTIONS.find((o) => o.value === primary.disclosureChoice)?.labelKey ??
      null;

    return (
      <Box>
        <RegisterReviewSection>
          <RegisterReviewSectionTitle>
            {t("affordable.application.specialNeeds.title")}
          </RegisterReviewSectionTitle>

          <Alert severity="info" sx={{ mb: 3 }}>
            {t("affordable.application.specialNeeds.primaryAnswerNote")}
          </Alert>

          <Box
            sx={{
              border: `1px solid ${COLORS.darkBlue[500]}`,
              backgroundColor: COLORS.blue[50],
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              {t("affordable.application.specialNeeds.selected")}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedLabelKey
                ? t(`affordable.application.specialNeeds.${selectedLabelKey}`)
                : primary.disclosureChoice}
            </Typography>
            {primary.isVeteran != null && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}
                >
                  {t("affordable.application.specialNeeds.veteranQuestion")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {primary.isVeteran
                    ? t("affordable.application.specialNeeds.veteranYes")
                    : t("affordable.application.specialNeeds.veteranNo")}
                </Typography>
              </>
            )}
          </Box>
        </RegisterReviewSection>

        <RegisterReviewSection>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {t("affordable.application.specialNeeds.acknowledgeLabel")}
              </Typography>
            }
            sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0 } }}
          />
          {!acknowledged && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              {t("affordable.application.specialNeeds.acknowledgeRequired")}
            </Typography>
          )}
        </RegisterReviewSection>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            mb: 4,
          }}
        >
          <Button variant="outlined" onClick={onBack} disabled={isSaving}>
            {t("account.leaseApplication.previous")}
          </Button>
          <Button
            variant="contained"
            disabled={!acknowledged || isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
            onClick={() =>
              onSubmit({ ...primary, _acknowledgedAt: Date.now() })
            }
          >
            {t("account.leaseApplication.next")}
          </Button>
        </Box>
      </Box>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.specialNeeds.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("affordable.application.specialNeeds.subtitle")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, fontWeight: 600 }}
        >
          {t("affordable.application.specialNeeds.categoriesIntro")}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 2 }}>
          {CATEGORY_KEYS.map((key) => (
            <Typography
              key={key}
              component="li"
              variant="body2"
              color="text.secondary"
            >
              {t(`affordable.application.specialNeeds.categories.${key}`)}
            </Typography>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("affordable.application.specialNeeds.disclosureNote")}
        </Typography>

        {/* TX Gov Code § 434.214 — required disclosure for tenant applications */}
        <Alert severity="info" icon={false} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t("affordable.application.specialNeeds.txVeteranNoticeTitle")}
          </Typography>
          <Typography variant="body2">
            {t("affordable.application.specialNeeds.txVeteranNotice")}{" "}
            <a
              href="https://veterans.portal.texas.gov"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://veterans.portal.texas.gov
            </a>
            .
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
          {t("affordable.application.specialNeeds.question")}
        </Typography>

        <Controller
          name="disclosureChoice"
          control={control}
          render={({ field }) => (
            <FormControl
              component="fieldset"
              fullWidth
              error={!!errors.disclosureChoice}
            >
              <RadioGroup {...field}>
                {OPTIONS.map((opt) => (
                  <Box
                    key={opt.value}
                    sx={{
                      border: `1px solid ${
                        field.value === opt.value
                          ? COLORS.darkBlue[500]
                          : COLORS.gray[200]
                      }`,
                      borderRadius: 2,
                      p: 2,
                      mb: 1.5,
                      backgroundColor:
                        field.value === opt.value
                          ? COLORS.blue[50]
                          : "transparent",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <FormControlLabel
                      value={opt.value}
                      control={<Radio />}
                      sx={{
                        alignItems: "center",
                        m: 0,
                        "& .MuiFormControlLabel-label": { flex: 1 },
                      }}
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t(
                            `affordable.application.specialNeeds.${opt.labelKey}`
                          )}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>
              {errors.disclosureChoice && (
                <FormHelperText>
                  {errors.disclosureChoice.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        <Typography variant="body1" sx={{ fontWeight: 500, mt: 4, mb: 2 }}>
          {t("affordable.application.specialNeeds.veteranQuestion")}
        </Typography>
        <Controller
          name="isVeteran"
          control={control}
          render={({ field }) => {
            const sel =
              field.value === true ? "yes" : field.value === false ? "no" : "";
            return (
              <FormControl
                component="fieldset"
                fullWidth
                error={!!errors.isVeteran}
              >
                <RadioGroup
                  value={sel}
                  onChange={(_, v) => field.onChange(v === "yes")}
                >
                  {[
                    {
                      v: "yes",
                      label: t(
                        "affordable.application.specialNeeds.veteranYes"
                      ),
                    },
                    {
                      v: "no",
                      label: t("affordable.application.specialNeeds.veteranNo"),
                    },
                  ].map((opt) => (
                    <Box
                      key={opt.v}
                      sx={{
                        border: `1px solid ${
                          sel === opt.v
                            ? COLORS.darkBlue[500]
                            : COLORS.gray[200]
                        }`,
                        borderRadius: 2,
                        p: 2,
                        mb: 1.5,
                        backgroundColor:
                          sel === opt.v ? COLORS.blue[50] : "transparent",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <FormControlLabel
                        value={opt.v}
                        control={<Radio />}
                        sx={{
                          alignItems: "center",
                          m: 0,
                          "& .MuiFormControlLabel-label": { flex: 1 },
                        }}
                        label={
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {opt.label}
                          </Typography>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
                {errors.isVeteran && (
                  <FormHelperText>{errors.isVeteran.message}</FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
      </RegisterReviewSection>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" onClick={onBack} disabled={isSaving}>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving || !isValid}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
